/**
 * The search's synonym groups (I-3024): the field `synonyms` of the core key
 * `search` of master's account config path — a map by language (`de`/`en`) of
 * a map by group name (`{ terms, oneWay, origin }`). `terms` holds 2–50 words;
 * `oneWay` makes `terms[0]` the source (searching it also finds the others,
 * not the other way round), `origin` is `own` or `import` (a synonym is never
 * learned). Like the spell-check words the layers merge to their UNION, a
 * group is written and removed one at a time with a merge patch, and a tenant
 * or company locks the field (`synonyms`) apart from the other fields of the
 * key — `learnCorrections` among them, which the „Korrekturen“ tab shows too.
 *
 * Master validates the document (a term in one group per language and layer,
 * fold twins); the helpers here only build and read it. Pure functions apart
 * from `useSearchSynonymsConfig`.
 */
import {
  useAccountConfig,
  type AccountConfigHandle,
  type AccountConfigLayer,
  type AccountConfigTarget,
  type EnforcedField,
  type UseAccountConfigOptions,
} from "./accountConfig";
import {
  dictionaryEntryPatch,
  dictionaryFieldLocked,
  dictionaryMapEntries,
  type DictionaryEntrySource,
} from "./dictionaryLayer";
import { isJsonObject } from "./mergePatch";
import type { DictionaryLanguage } from "../text/dictionaryFile";

/** The core key of the search's settings. */
export const SEARCH_CONFIG_KEY = "search";

/** The field of the synonym groups. */
export const SEARCH_SYNONYMS_FIELD = "synonyms";

/** The field that switches learning from accepted suggestions on or off. */
export const SEARCH_LEARN_CORRECTIONS_FIELD = "learnCorrections";

/** Where a group comes from: typed in or imported. */
export type SearchSynonymOrigin = "own" | "import";

/** One group: the words, whether the first one is the only source, where it comes from. */
export interface SearchSynonymGroup {
  terms: string[];
  oneWay: boolean;
  origin: SearchSynonymOrigin;
}

/** `search` as far as the dictionary reads it (the rest of the key is the search settings' own). */
export interface SearchSynonymsConfig {
  learnCorrections?: boolean;
  synonyms?: Partial<Record<DictionaryLanguage, Record<string, SearchSynonymGroup | null>>>;
}

/** One row of the synonym list. */
export interface SearchSynonymEntry extends SearchSynonymGroup {
  lang: DictionaryLanguage;
  name: string;
  source: DictionaryEntrySource;
}

/** `search` of the session; `layers` reads the stored layers too (the dictionary page needs them). */
export function useSearchSynonymsConfig(
  options: Pick<UseAccountConfigOptions<SearchSynonymsConfig>, "layers"> = {},
): AccountConfigHandle<SearchSynonymsConfig> {
  return useAccountConfig<SearchSynonymsConfig>(SEARCH_CONFIG_KEY, options);
}

function readGroup(value: unknown): SearchSynonymGroup | undefined {
  if (!isJsonObject(value) || !Array.isArray(value.terms)) return undefined;
  const terms = value.terms.filter((term): term is string => typeof term === "string");
  if (!terms.length) return undefined;
  return {
    terms,
    oneWay: value.oneWay === true,
    origin: value.origin === "import" ? "import" : "own",
  };
}

/** Whether a tenant or company locked the synonyms. */
export function searchSynonymsLocked(enforced: readonly EnforcedField[]): boolean {
  return dictionaryFieldLocked(enforced, SEARCH_SYNONYMS_FIELD);
}

/** Whether a tenant or company fixed `search.learnCorrections`. */
export function searchLearnCorrectionsLocked(enforced: readonly EnforcedField[]): boolean {
  return dictionaryFieldLocked(enforced, SEARCH_LEARN_CORRECTIONS_FIELD);
}

/** The resolved `learnCorrections`: on unless it is switched off (the platform default is on). */
export function searchLearnCorrections(config: SearchSynonymsConfig | undefined): boolean {
  return config?.learnCorrections !== false;
}

/**
 * The effective groups by language and name, each with the layer it comes
 * from (`own` where the session's layer holds the name; without the layers
 * every group is own).
 */
export function searchSynonymEntries(
  config: SearchSynonymsConfig | undefined,
  layers: readonly AccountConfigLayer[] | null | undefined,
  target: AccountConfigTarget | null | undefined,
): SearchSynonymEntry[] {
  return dictionaryMapEntries(config, SEARCH_SYNONYMS_FIELD, layers, target, readGroup).map(
    (entry) => ({ lang: entry.lang, name: entry.key, ...entry.value, source: entry.source }),
  );
}

/** The merge patch that writes one group; `renamedFrom` is the name it had before (that key goes to `null`). */
export function searchSynonymPatch(
  lang: DictionaryLanguage,
  name: string,
  value: SearchSynonymGroup,
  renamedFrom?: string,
): SearchSynonymsConfig {
  return dictionaryEntryPatch(SEARCH_SYNONYMS_FIELD, lang, name, value, renamedFrom) as SearchSynonymsConfig;
}

/** The merge patch that removes one group from the layer that is written. */
export function searchSynonymRemoval(lang: DictionaryLanguage, name: string): SearchSynonymsConfig {
  return dictionaryEntryPatch(SEARCH_SYNONYMS_FIELD, lang, name, null) as SearchSynonymsConfig;
}
